/* Search V2: accessible predictive search with explicit states and Theme Editor-safe lifecycle. */
(function () {
  'use strict';
  if (customElements.get('bb-search')) return;

  class BBSearch extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) this.lifecycle.abort();
      this.lifecycle = new AbortController();
      const signal = this.lifecycle.signal;

      this.btn = this.querySelector('.bb-header__search-btn');
      this.panel = this.querySelector('.bb-search-panel');
      this.form = this.querySelector('.bb-search-form');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.bb-predictive');
      this.closeBtn = this.querySelector('[data-bb-search-close]');
      this.clearBtn = this.querySelector('[data-bb-search-clear]');
      if (!this.btn || !this.panel || !this.form || !this.input || !this.results || !this.closeBtn) return;

      this.minChars = Number(this.getAttribute('data-min-chars')) || 2;
      this.loadingText = this.getAttribute('data-loading') || 'Laden…';
      this.errorText = this.getAttribute('data-error') || 'Er ging iets mis. Probeer het opnieuw.';
      this.abort = null;
      this.requestId = 0;
      this.activeIndex = -1;
      this.mobile = window.matchMedia('(max-width: 1023px)');
      this.onMediaChange = () => this.syncPageLock();
      this.debouncedQuery = this.debounce(() => this.query(), 220);

      this.btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.setOpen(this.panel.hidden);
      }, { signal });
      this.closeBtn.addEventListener('click', () => this.setOpen(false), { signal });
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.clear(), { signal });
      this.input.addEventListener('input', () => this.onInput(), { signal });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e), { signal });
      this.panel.addEventListener('keydown', (e) => this.onPanelKeydown(e), { signal });
      this.results.addEventListener('mousemove', (e) => this.onPointerMove(e), { signal });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.panel.hidden) this.setOpen(false);
      }, { signal });

      document.addEventListener('click', (e) => {
        if (!this.panel.hidden && !this.mobile.matches && !this.contains(e.target)) this.setOpen(false, false);
      }, { signal });

      this.mobile.addEventListener?.('change', this.onMediaChange);
      this.syncClear();
    }

    disconnectedCallback() {
      if (this.lifecycle) this.lifecycle.abort();
      this.lifecycle = null;
      if (this.abort) this.abort.abort();
      this.abort = null;
      this.debouncedQuery?.cancel?.();
      if (this.mobile && this.onMediaChange) this.mobile.removeEventListener?.('change', this.onMediaChange);
      document.body.classList.remove('bb-search-open');
    }

    setOpen(open, restoreFocus = true) {
      if (!this.panel) return;
      this.panel.hidden = !open;
      this.btn.setAttribute('aria-expanded', String(open));
      this.input.setAttribute('aria-expanded', String(open));
      this.syncPageLock();

      if (open) {
        this.input.focus();
        if (this.input.value.trim().length >= this.minChars && this.results.childElementCount === 0) this.query();
      } else {
        if (this.abort) this.abort.abort();
        this.requestId += 1;
        this.resetActive();
        if (restoreFocus) this.btn.focus();
      }
    }

    syncPageLock() {
      if (!this.panel || !this.mobile) return;
      const modal = !this.panel.hidden && this.mobile.matches;
      document.body.classList.toggle('bb-search-open', modal);
      if (modal) {
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
      } else {
        this.panel.removeAttribute('role');
        this.panel.removeAttribute('aria-modal');
      }
    }

    onPanelKeydown(e) {
      if (e.key !== 'Tab' || this.panel.hidden || !this.mobile.matches) return;
      const focusable = Array.from(this.panel.querySelectorAll(
        'a[href], button:not([disabled]):not([hidden]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    onInput() {
      this.syncClear();
      this.resetActive();
      if (this.abort) this.abort.abort();
      this.requestId += 1;
      this.results.removeAttribute('aria-busy');
      this.results.innerHTML = '';
      const q = this.input.value.trim();
      if (q.length < this.minChars) return;
      this.setState(this.loadingText, true);
      this.debouncedQuery();
    }

    query() {
      const q = this.input.value.trim();
      if (q.length < this.minChars) return;
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const id = ++this.requestId;
      this.results.setAttribute('aria-busy', 'true');

      const base = window.BB.config.routes.predictiveSearch;
      const params = new URLSearchParams({
        q,
        'resources[type]': 'product,collection',
        'resources[limit]': '8',
        'resources[limit_scope]': 'all',
        section_id: 'predictive-search'
      });

      fetch(base + '?' + params.toString(), {
        signal: this.abort.signal,
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('Predictive search request failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          if (id !== this.requestId || q !== this.input.value.trim()) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('#shopify-section-predictive-search');
          this.results.innerHTML = inner ? inner.innerHTML : '';
          this.results.removeAttribute('aria-busy');
          this.resetActive();
        })
        .catch((error) => {
          if (error.name === 'AbortError' || id !== this.requestId) return;
          this.results.removeAttribute('aria-busy');
          this.results.innerHTML = '';
          this.setState(this.errorText, false);
        });
    }

    setState(text, loading) {
      this.results.innerHTML = '';
      if (loading) this.results.setAttribute('aria-busy', 'true');
      const state = document.createElement('div');
      state.className = 'bb-search-state';
      state.setAttribute('role', 'status');
      state.textContent = text;
      this.results.appendChild(state);
    }

    clear() {
      if (this.abort) this.abort.abort();
      this.requestId += 1;
      this.input.value = '';
      this.results.innerHTML = '';
      this.results.removeAttribute('aria-busy');
      this.resetActive();
      this.syncClear();
      this.input.focus();
    }

    syncClear() {
      if (this.clearBtn) this.clearBtn.hidden = this.input.value.length === 0;
    }

    options() {
      return Array.from(this.results.querySelectorAll('[data-search-option]'));
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.input.value && this.mobile.matches) this.clear();
        else this.setOpen(false);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const options = this.options();
      if (!options.length) return;

      if (e.key === 'Enter') {
        if (this.activeIndex >= 0) {
          const link = options[this.activeIndex].querySelector('a[href]');
          if (link) {
            e.preventDefault();
            window.location.assign(link.href);
          }
        }
        return;
      }

      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
      this.applyActive(options);
    }

    onPointerMove(e) {
      const option = e.target.closest('[data-search-option]');
      if (!option) return;
      const options = this.options();
      const index = options.indexOf(option);
      if (index < 0 || index === this.activeIndex) return;
      this.activeIndex = index;
      this.applyActive(options);
    }

    applyActive(options) {
      options.forEach((option, index) => {
        const active = index === this.activeIndex;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', String(active));
      });
      const active = options[this.activeIndex];
      if (active) {
        if (!active.id) active.id = 'bb-search-option-' + this.activeIndex;
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    resetActive() {
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.options().forEach((option) => {
        option.classList.remove('is-active');
        option.setAttribute('aria-selected', 'false');
      });
    }

    debounce(fn, ms) {
      let timer = null;
      const debounced = (...args) => {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          timer = null;
          fn(...args);
        }, ms);
      };
      debounced.cancel = () => {
        if (timer) window.clearTimeout(timer);
        timer = null;
      };
      return debounced;
    }
  }

  customElements.define('bb-search', BBSearch);
})();
